import React, { useEffect, useMemo, useState } from 'react';
import { p } from '../i18n/useP';
import { getTimeEffective } from '../api/hrTimeEffective';
import { formatNumber } from '../utils/format';
import './TimeBridgeChart.css';

/**
 * Pont des temps — cascade TT → TO → TR du client connecté, en graphique pont
 * (waterfall). Six colonnes : trois paliers (TT, TO, TR) et trois déductions
 * repérées A, B, C, détaillées en légende sous le graphique.
 *
 * Les lettres portent le graphique, la légende porte les libellés : « congés
 * spéciaux (absences planifiées et indemnisées) » ne tient pas sous une colonne.
 *
 * ARITHMÉTIQUE — les hauteurs ET les valeurs affichées sont dérivées des
 * PALIERS (TT, TO, TR), jamais des composants saisis :
 *   A = TT − TO   ·   B + C = TO − TR
 * Le moteur arrondit les vacances puis le TO par personne, et vingt arrondis
 * ne se somment pas comme l'arrondi de la somme : la somme des droits
 * individuels peut s'écarter de TT − TO de quelques heures. Un pont doit
 * tomber juste — on le construit donc sur les paliers, qui font foi.
 */

const W = 720;          // largeur du viewBox
const TOP = 22;         // marge haute (valeurs au-dessus des colonnes)
const BASE = 322;       // ligne de sol
const PLOT = BASE - TOP;
const COLS = 6;
const GAP = 40;
const CW = (W - GAP * (COLS - 1)) / COLS;   // largeur d'une colonne

export default function TimeBridgeChart({ tenantId, year }) {
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tenantId) { setTotals(null); return; }
    setLoading(true); setError(null);
    getTimeEffective(tenantId, year)
      .then(r => r.error ? setError(r.error) : setTotals(r.data?.totals || null))
      .catch(e => setError(e.message || p('Erreur réseau')))
      .finally(() => setLoading(false));
  }, [tenantId, year]);

  const model = useMemo(() => {
    if (!totals) return null;
    const n = (k) => Number(totals[k]) || 0;
    const tt = n('tt_net');
    const to = n('ot_hours');
    const tr = n('tr_hours');
    if (tt <= 0) return null;

    const vac = tt - to;                                   // palier → palier
    const spe = n('special_leave_hours');
    const ges = to - tr - spe;                             // gestion = le solde du palier

    const yMax = tt * 1.04;
    const y = (v) => BASE - (v * PLOT) / yMax;
    const x = (i) => i * (CW + GAP);

    // Six colonnes : palier, coupe, palier, coupe, coupe, palier.
    const bars = [
      { i: 0, top: tt,       bottom: 0,        v: tt,  kind: 'level', lb: 'TT', sub: p('Temps total') },
      { i: 1, top: tt,       bottom: to,       v: vac, kind: 'cut',   lb: 'A' },
      { i: 2, top: to,       bottom: 0,        v: to,  kind: 'level', lb: 'TO', sub: p('Temps ouvert') },
      { i: 3, top: to,       bottom: to - spe, v: spe, kind: 'cut',   lb: 'B' },
      { i: 4, top: to - spe, bottom: tr,       v: ges, kind: 'cut',   lb: 'C' },
      { i: 5, top: tr,       bottom: 0,        v: tr,  kind: 'level', lb: 'TR', sub: p('Capacité pratique') },
    ].map(b => ({ ...b, x: x(b.i), yTop: y(b.top), h: Math.max(0, y(b.bottom) - y(b.top)) }));

    return {
      bars, tt, to, tr, vac, spe, ges,
      // Connecteur : du bord droit d'une colonne au bord gauche de la suivante,
      // au niveau où la première s'arrête.
      links: bars.slice(0, -1).map((b, i) => ({
        x1: b.x + CW, x2: bars[i + 1].x, y: y(b.bottom === 0 ? b.top : b.bottom),
      })),
    };
  }, [totals]);

  if (!tenantId) return null;
  if (loading) return <div className="tbc__state">{p('Chargement…')}</div>;
  if (error) return <div className="tbc__state tbc__state--err">{error}</div>;
  if (!model) return <div className="tbc__state">{p('Aucune donnée de temps pour cet exercice.')}</div>;

  const h = (v) => formatNumber(v, 0);
  const legend = [
    { k: 'A', t: p('Vacances payées'), d: p('Droit porté par chaque contrat'), v: model.vac },
    { k: 'B', t: p('Congés spéciaux'), d: p('Absences planifiées et indemnisées : maternité, service'), v: model.spe },
    { k: 'C', t: p('Temps de gestion'), d: p('Administration, direction, supervision de production'), v: model.ges },
  ];

  return (
    <div className="tbc">
      <div className="tbc__scroll">
        <svg className="tbc__svg" viewBox={`0 0 ${W} 376`} role="img"
             aria-label={`${p('Pont des temps')} ${year} : TT ${h(model.tt)} h, TO ${h(model.to)} h, TR ${h(model.tr)} h.`}>
          {model.links.map((l, i) => (
            <line key={i} className="tbc__link" x1={l.x1} y1={l.y} x2={l.x2} y2={l.y} />
          ))}
          {model.bars.map(b => (
            <g key={b.i}>
              {b.h > 0 && (
                <rect className={`tbc__bar tbc__bar--${b.kind}`}
                      x={b.x} y={b.yTop} width={CW} height={b.h} rx="3" />
              )}
              {/* Une déduction nulle garde sa lettre mais n'écrit pas « −0 » :
                  la légende dit « — », le graphique se tait. */}
              {(b.kind === 'level' || b.v > 0) && (
                <text className={`tbc__val tbc__val--${b.kind}`} x={b.x + CW / 2} y={b.yTop - 8}>
                  {b.kind === 'cut' ? `−${h(b.v)}` : h(b.v)}
                </text>
              )}
              <text className="tbc__lb" x={b.x + CW / 2} y={BASE + 22}>{b.lb}</text>
              {b.sub && <text className="tbc__lb2" x={b.x + CW / 2} y={BASE + 38}>{b.sub}</text>}
            </g>
          ))}
          <line className="tbc__base" x1="0" y1={BASE} x2={W} y2={BASE} />
        </svg>
      </div>

      <ul className="tbc__legend">
        {legend.map(l => (
          <li key={l.k}>
            <span className="tbc__key">{l.k}</span>
            <span className="tbc__txt"><b>{l.t}</b><small>{l.d}</small></span>
            <span className="tbc__num">{l.v ? `−${h(l.v)} h` : '—'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
